import {PrismicNextImage} from '@prismicio/next'
import {PrismicRichText} from '@prismicio/react'
import {FourGridSliceGrid} from 'prismicio-types'
import {PropsWithChildren} from 'react'
import {Gradient} from 'slices/SideGrid/components'
import {Container} from 'src/common/Container'

type FourGridCardsProps = {
  slice: FourGridSliceGrid & {
    slice_type: string
    variation: string
  }
}

export const GridVariation = ({slice}: FourGridCardsProps) => {
  return (
    <Container
      backgroundColor={slice.primary.background_color}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="flex-col items-center justify-center !py-10 !pb-0 md:!pt-12"
    >
      <div className="w-full max-w-[1200px] px-5 pb-3 md:px-10 lg:px-5 lg:pb-6 xl:px-0" data-aos="fade-up">
        <div className="flex flex-col gap-4 lg:gap-6">
          <span className="text-button-s text-ui-blue-450">{slice.primary.tag}</span>
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading2: ({children}) => (
                <h3 className="lg:text-tg-h2 text-32 leading-[normal] tracking-[-.32px] text-moss-black">{children}</h3>
              ),
              strong: ({children}) => <span className="text-moss-grey-400">{children}</span>,
            }}
          />
        </div>
      </div>
      <Grid>
        {slice.primary.cells.map((cell, index) => (
          <div
            key={`card-${index}`}
            className="col-span-1 ml-[-1px] mt-[-1px] flex h-full min-h-0 flex-col items-center gap-6 rounded-[4px] border border-moss-grey-150 p-4 lg:gap-8 lg:p-6"
          >
            <div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden">
              <PrismicNextImage field={cell.image} className="max-h-full max-w-full object-contain" />
            </div>
            <div className="w-full shrink-0">
              <PrismicRichText
                field={cell.content}
                components={{
                  paragraph: ({children}) => <p className="text-detail text-center text-moss-grey-400">{children}</p>,
                  strong: ({children}) => <span className="text-moss-black">{children}</span>,
                }}
              />
            </div>
          </div>
        ))}
      </Grid>
    </Container>
  )
}

const gridClass =
  'grid grid-cols-[20px_1fr_20px] grid-rows-[40px_repeat(4,168px)_40px] md:grid-cols-[40px_1fr_1fr_40px] md:grid-rows-[40px_repeat(2,168px)_40px] lg:grid-cols-[auto_repeat(4,minmax(auto,301px))_auto] lg:grid-rows-[64px_220px_80px]'

const Grid = ({children}: PropsWithChildren) => {
  return (
    <div className={`relative w-full ${gridClass}`} data-aos="fade-up">
      <Gradient color="255, 255, 255" gradientStart={10} height={40} location="top" />
      <Separators />
      {children}
      <Gradient color="255, 255, 255" gradientStart={10} height={40} location="bottom" />
    </div>
  )
}

const Separators = () => (
  <>
    {/* Left separator */}
    <div className="col-span-1 col-start-1 row-start-1 rounded-br-[4px] border-b border-r border-moss-grey-150" />
    <div className="col-span-1 col-start-1 row-start-2 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150" />
    <div className="col-span-1 col-start-1 row-start-3 mt-[-1px] rounded-r-[4px] border-r border-t border-moss-grey-150 lg:hidden" />
    <div className="col-span-1 col-start-1 row-start-4 mt-[-1px] rounded-r-[4px] border-r border-t border-moss-grey-150 md:hidden" />
    <div className="col-span-1 col-start-1 row-start-5 mt-[-1px] rounded-r-[4px] border-r border-t border-moss-grey-150 md:hidden" />
    <div className="col-span-1 col-start-1 row-start-6 mt-[-1px] rounded-tr-[4px] border-r border-t border-moss-grey-150 md:row-start-4 lg:row-start-3" />

    {/* Top separator */}
    <div className="col-span-1 col-start-2 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150" />
    <div className="col-span-1 col-start-3 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-md:hidden" />
    <div className="col-span-1 col-start-4 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-lg:hidden" />
    <div className="col-span-1 col-start-5 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-lg:hidden" />

    {/* Bottom separator */}
    <div className="col-span-1 col-start-2 row-start-6 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 md:row-start-4 lg:row-start-3" />
    <div className="col-span-1 col-start-3 row-start-6 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 max-md:hidden md:row-start-4 lg:row-start-3" />
    <div className="col-span-1 col-start-4 row-start-6 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 max-lg:hidden lg:row-start-3" />
    <div className="col-span-1 col-start-5 row-start-6 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 max-lg:hidden lg:row-start-3" />

    {/* Right separator */}
    <div className="col-span-1 col-start-3 row-start-1 ml-[-1px] rounded-bl-[4px] border-b border-l border-moss-grey-150 md:col-start-4 lg:col-start-6" />
    <div className="col-span-1 col-start-3 row-start-2 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:col-start-4 lg:col-start-6 lg:row-start-2" />
    <div className="col-span-1 col-start-3 row-start-3 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:col-start-4 lg:col-start-6 lg:row-start-2 lg:hidden" />
    <div className="col-span-1 col-start-3 row-start-4 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:col-start-4 md:hidden lg:col-start-6 lg:row-start-2" />
    <div className="col-span-1 col-start-3 row-start-5 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 md:col-start-4 md:hidden lg:col-start-6 lg:row-start-2" />
    <div className="col-span-1 col-start-3 row-start-6 ml-[-1px] mt-[-1px] rounded-l-[4px] border-l border-t border-moss-grey-150 md:col-start-4 md:row-start-4 lg:col-start-6 lg:row-start-3" />
  </>
)
